import { useCallback, useMemo, useRef, useState } from 'react';
import { SxStyle } from '../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Grid2 as Grid } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { MatchConfig } from '../utils/data';
import { getEditPlayerConfigSchema } from '../UI-schema';
import { doc, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../firebase-app';
import { isAllFieldsValid } from '../../../../utils/team';
import { getMatchesConfigPath } from '../../../../utils/paths';
import { useDocumentData } from 'react-firebase-hooks/firestore';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  leagueAbbrv: string;
  season: string;
  matchId: number;
  playerId: string;
  matchConfig?: MatchConfig;
}

const styles: SxStyle = {
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: '100px',
  },
};

function EditPlayerConfigModal({
  open,
  onClose,
  onDone,
  leagueAbbrv,
  season,
  matchId,
  playerId,
}: Props) {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>({});
  const editedFields = useRef<Record<string, boolean>>({});
  const matchesConfigPath = getMatchesConfigPath(leagueAbbrv, season);

  const [matchConfig, matchConfigLoading] = useDocumentData(
    doc(firebaseApp.db, `${matchesConfigPath}/${matchId}`)
  );

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      const updatedMatchConfig: Partial<MatchConfig> = {
        id: matchId,
        hidePlayerStats: {
          [playerId]: {
            maxSpeed:
              valuesFields.current.hideMaxSpeed !== undefined
                ? valuesFields.current.hideMaxSpeed
                : matchConfig?.hidePlayerStats?.maxSpeed,
            distance:
              valuesFields.current.hideDistance !== undefined
                ? valuesFields.current.hideDistance
                : matchConfig?.hidePlayerStats?.distance,
          },
        },
      };

      await setDoc(doc(firebaseApp.db, `${matchesConfigPath}/${matchId}`), updatedMatchConfig, {
        merge: true,
      });

      onDone();
      onClose?.();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.saveError, {
        variant: 'error',
      });
    }
    setSubmitLoading(false);
  }, [
    matchConfig,
    matchId,
    playerId,
    onDone,
    onClose,
    enqueueSnackbar,
    strings,
    matchesConfigPath,
  ]);

  const editPlayerConfigUiScheme = useMemo(() => {
    if (!matchConfigLoading) {
      return getEditPlayerConfigSchema(matchConfig?.hidePlayerStats?.[playerId]);
    }
    return null;
  }, [matchConfigLoading, matchConfig, playerId]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.matches.matchSettings}>
      <DialogContent sx={styles.contentList} dividers>
        <Grid container spacing={3} direction="column">
          {editPlayerConfigUiScheme?.fields.map((field) => (
            <Grid size={12} key={field.id}>
              <RenderModuleField
                field={field}
                setChange={onChange}
                setValidation={onValidation}
                path="matches"
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled && !isDirty}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.save}
        </LoadingButton>
      </DialogActions>
    </BaseDialog>
  );
}

export default EditPlayerConfigModal;
